import { useState, useEffect, useRef } from 'react'
import { fetchWithCsrf } from '../utils/csrf'
import Modal from './Modal'

interface RecommendedBook {
  id: number
  title: string
  author: string
  description?: string
  cover_url?: string
  genre?: string
  first_publish_year?: number
  number_of_pages?: number
  recommendation_reason?: string
}

interface Message {
  id: number
  role: 'user' | 'assistant'
  content: string
  recommended_books: RecommendedBook[]
  created_at: string
}

interface Conversation {
  id: number
  status: string
  created_at: string
  messages: Message[]
}

// Club mode props
interface ClubModeProps {
  mode: 'club'
  clubId: number
  clubName: string
  onClose: () => void
  onBookChosen?: () => void
}

// Personal mode props
interface PersonalModeProps {
  mode: 'personal'
  userName: string
  onClose: () => void
  onBookChosen?: () => void
}

type RecommendationChatProps = ClubModeProps | PersonalModeProps

export default function RecommendationChat(props: RecommendationChatProps) {
  const { mode, onClose, onBookChosen } = props
  const isClubMode = mode === 'club'

  const [conversation, setConversation] = useState<Conversation | null>(null)
  const [loading, setLoading] = useState(true)
  const [sending, setSending] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [actionInProgress, setActionInProgress] = useState<{ bookId: number; action: string } | null>(null)
  const [completedActions, setCompletedActions] = useState<Record<number, string>>({}) // bookId -> action
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  // Build API base path
  const apiBasePath = isClubMode
    ? `/api/v1/clubs/${(props as ClubModeProps).clubId}/recommendations`
    : '/api/v1/personal_recommendations'

  const displayName = isClubMode
    ? (props as ClubModeProps).clubName
    : (props as PersonalModeProps).userName

  useEffect(() => {
    fetchConversation()
  }, [apiBasePath])

  useEffect(() => {
    scrollToBottom()
  }, [conversation?.messages])

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  const fetchConversation = async () => {
    try {
      setLoading(true)
      const response = await fetch(`${apiBasePath}/conversation`)
      if (!response.ok) throw new Error('Failed to load conversation')
      const data = await response.json()
      setConversation(data.conversation)
    } catch (err) {
      setError('Failed to load conversation')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  const handleSend = async () => {
    if (!message.trim() || sending) return

    const userMessage = message.trim()
    setMessage('')
    setSending(true)
    setError('')

    // Optimistically add user message
    if (conversation) {
      setConversation({
        ...conversation,
        messages: [
          ...conversation.messages,
          {
            id: Date.now(),
            role: 'user',
            content: userMessage,
            recommended_books: [],
            created_at: new Date().toISOString()
          }
        ]
      })
    }

    try {
      const response = await fetchWithCsrf(`${apiBasePath}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message: userMessage })
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Failed to send message')
      }

      const data = await response.json()
      setConversation(data.conversation)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send message')
      // Remove optimistic message on error
      if (conversation) {
        setConversation({
          ...conversation,
          messages: conversation.messages.filter(m => m.id !== Date.now())
        })
      }
    } finally {
      setSending(false)
      inputRef.current?.focus()
    }
  }

  const handleNewConversation = async () => {
    try {
      setLoading(true)
      const response = await fetchWithCsrf(`${apiBasePath}/new_conversation`, {
        method: 'POST'
      })

      if (!response.ok) throw new Error('Failed to start new conversation')
      const data = await response.json()
      setConversation(data.conversation)
    } catch (err) {
      setError('Failed to start new conversation')
    } finally {
      setLoading(false)
    }
  }

  const handleBookAction = async (bookId: number, action: string) => {
    try {
      setActionInProgress({ bookId, action })

      let endpoint: string
      let body = { book_id: bookId }

      if (isClubMode) {
        if (action === 'pick') {
          endpoint = `${apiBasePath}/choose_book`
        } else if (action === 'suggest') {
          endpoint = `${apiBasePath}/suggest_book`
        } else {
          throw new Error('Unknown action')
        }
      } else {
        // Personal mode
        if (action === 'toRead') {
          endpoint = `${apiBasePath}/add_to_read`
        } else if (action === 'startReading') {
          endpoint = `${apiBasePath}/start_reading`
        } else {
          throw new Error('Unknown action')
        }
      }

      const response = await fetchWithCsrf(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      })

      if (!response.ok) {
        const data = await response.json()
        throw new Error(data.error || 'Action failed')
      }

      // Track the completed action
      setCompletedActions(prev => ({ ...prev, [bookId]: action }))
      onBookChosen?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Action failed')
    } finally {
      setActionInProgress(null)
    }
  }

  const getBookActionStatus = (bookId: number) => {
    return completedActions[bookId] || null
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const suggestedPrompts = isClubMode
    ? [
        "Recommend 3 books for Halloween - something scary!",
        "What's a good book similar to what we've read?",
        "Find me something funny and light"
      ]
    : [
        "Recommend something based on my reading history",
        "I'm in the mood for something like my favorite books",
        "Suggest something different from what I usually read"
      ]

  return (
    <Modal isOpen={true} onClose={onClose} size="lg" showCloseButton={false} className="h-[80vh] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Book Recommendations</h2>
          <p className="text-sm text-gray-600">
            {isClubMode ? `AI-powered suggestions for ${displayName}` : `Personal recommendations for ${displayName}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {conversation && conversation.messages.length > 0 && (
            <button
              onClick={handleNewConversation}
              className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              title="Start new conversation"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </button>
          )}
          <button
            onClick={onClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

        {/* Messages */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {loading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
            </div>
          ) : conversation?.messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-full flex items-center justify-center mb-4">
                <svg className="w-8 h-8 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {isClubMode ? "What should we read next?" : "What are you in the mood for?"}
              </h3>
              <p className="text-gray-600 max-w-sm">
                Ask me for book recommendations! Try something like:
              </p>
              <div className="mt-4 space-y-2">
                {suggestedPrompts.map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => setMessage(suggestion)}
                    className="block w-full text-left px-4 py-2 text-sm text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors"
                  >
                    "{suggestion}"
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <>
              {conversation?.messages.map((msg) => (
                <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] ${msg.role === 'user' ? 'order-2' : 'order-1'}`}>
                    {msg.role === 'assistant' && (
                      <div className="flex items-center gap-2 mb-1">
                        <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                          </svg>
                        </div>
                        <span className="text-xs text-gray-500">Book Assistant</span>
                      </div>
                    )}
                    <div
                      className={`rounded-2xl px-4 py-3 ${
                        msg.role === 'user'
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-900'
                      }`}
                    >
                      <p className="whitespace-pre-wrap">{msg.content}</p>
                    </div>

                    {/* Recommended Books */}
                    {msg.recommended_books && msg.recommended_books.length > 0 && (
                      <div className="mt-3 space-y-3">
                        {msg.recommended_books.map((book) => (
                          <div key={book.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm">
                            <div className="flex gap-4">
                              {book.cover_url ? (
                                <img
                                  src={book.cover_url}
                                  alt={book.title}
                                  className="w-16 h-24 object-cover rounded shadow-sm flex-shrink-0"
                                />
                              ) : (
                                <div className="w-16 h-24 bg-gradient-to-br from-indigo-100 to-purple-100 rounded flex items-center justify-center flex-shrink-0">
                                  <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                                  </svg>
                                </div>
                              )}
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-gray-900">{book.title}</h4>
                                <p className="text-sm text-gray-600">by {book.author}</p>
                                {book.genre && (
                                  <span className="inline-block mt-1 px-2 py-0.5 text-xs font-medium text-indigo-700 bg-indigo-100 rounded">
                                    {book.genre}
                                  </span>
                                )}
                                {book.recommendation_reason && (
                                  <p className="text-sm text-gray-700 mt-2 italic">
                                    {book.recommendation_reason}
                                  </p>
                                )}
                                {book.description && (
                                  <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                                    {book.description}
                                  </p>
                                )}

                                {/* Action buttons - different for club vs personal */}
                                <div className="mt-3 flex gap-2 flex-wrap">
                                  {(() => {
                                    const completedAction = getBookActionStatus(book.id)

                                    if (completedAction) {
                                      // Show completed state
                                      const labels: Record<string, string> = {
                                        pick: 'Added as Pick',
                                        suggest: 'Added to Suggestions',
                                        startReading: 'Started Reading',
                                        toRead: 'Added to Want to Read'
                                      }
                                      return (
                                        <span className="px-3 py-1.5 text-sm font-medium text-green-700 bg-green-100 rounded-lg flex items-center gap-1.5">
                                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                          </svg>
                                          {labels[completedAction]}
                                        </span>
                                      )
                                    }

                                    if (isClubMode) {
                                      return (
                                        <>
                                          <button
                                            onClick={() => handleBookAction(book.id, 'pick')}
                                            disabled={actionInProgress !== null}
                                            className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                          >
                                            {actionInProgress?.bookId === book.id && actionInProgress?.action === 'pick' ? 'Adding...' : 'Add as Pick'}
                                          </button>
                                          <button
                                            onClick={() => handleBookAction(book.id, 'suggest')}
                                            disabled={actionInProgress !== null}
                                            className="px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                          >
                                            {actionInProgress?.bookId === book.id && actionInProgress?.action === 'suggest' ? 'Adding...' : 'Suggest'}
                                          </button>
                                        </>
                                      )
                                    } else {
                                      return (
                                        <>
                                          <button
                                            onClick={() => handleBookAction(book.id, 'startReading')}
                                            disabled={actionInProgress !== null}
                                            className="px-3 py-1.5 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                          >
                                            {actionInProgress?.bookId === book.id && actionInProgress?.action === 'startReading' ? 'Starting...' : 'Start Reading'}
                                          </button>
                                          <button
                                            onClick={() => handleBookAction(book.id, 'toRead')}
                                            disabled={actionInProgress !== null}
                                            className="px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                          >
                                            {actionInProgress?.bookId === book.id && actionInProgress?.action === 'toRead' ? 'Adding...' : 'Want to Read'}
                                          </button>
                                        </>
                                      )
                                    }
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="max-w-[85%]">
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-6 h-6 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                      </div>
                      <span className="text-xs text-gray-500">Book Assistant</span>
                    </div>
                    <div className="bg-gray-100 rounded-2xl px-4 py-3">
                      <div className="flex items-center gap-2">
                        <div className="animate-bounce w-2 h-2 bg-indigo-600 rounded-full"></div>
                        <div className="animate-bounce w-2 h-2 bg-indigo-600 rounded-full" style={{ animationDelay: '0.1s' }}></div>
                        <div className="animate-bounce w-2 h-2 bg-indigo-600 rounded-full" style={{ animationDelay: '0.2s' }}></div>
                        <span className="text-sm text-gray-600 ml-2">Finding great books for you...</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        {/* Error */}
        {error && (
          <div className="px-4 py-2 bg-red-50 border-t border-red-200">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Input */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Ask for book recommendations..."
              rows={1}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={sending}
            />
            <button
              onClick={handleSend}
              disabled={!message.trim() || sending}
              className="px-4 py-3 bg-gradient-to-r from-indigo-500 to-purple-500 text-white rounded-xl hover:from-indigo-600 hover:to-purple-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
              </svg>
            </button>
          </div>
        </div>
      </Modal>
  )
}
